import React, { useEffect, useRef, useState } from 'react';
import Autocomplete from 'react-google-autocomplete';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import {
  VStack,
  Flex,
  Text,
  Wrap,
  Input,
  Button,
  useToast,
} from '@chakra-ui/react';
import { css } from '@emotion/react';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  setCurrentStep,
  setStep3,
  useAppDispatch,
  useAppSelector,
  useCreateNewScheduleMutation,
} from '@spartalabs/pdc-core';
import { Card, SimpleInput } from 'components';
import { addDays } from 'date-fns';
import { DelayAnimation } from 'shared/DelayAnimation';
import * as z from 'zod';
import { ConfirmModal } from '../ConfirmModal';
import { NewScheduleHeader } from '../NewScheduleHeader';
import './styles.css';

const ScheduleFormSchema = z.object({
  scheduleDate: z
    .string({
      required_error: 'Campo obrigatório',
    })
    .nonempty({ message: 'Campo obrigatório' }),
  scheduleTime: z.string().nonempty({ message: 'Campo obrigatório' }),
  address: z
    .string({
      required_error: 'Campo obrigatório',
    })
    .nonempty({ message: 'Campo obrigatório' }),
  name: z.string().nonempty({ message: 'Campo obrigatório' }),
  email: z
    .string()
    .nonempty('Campo obrigatório')
    .email('Email inválido')
    .optional(),
  phone: z
    .string({
      required_error: 'Campo obrigatório',
    })
    .nonempty({ message: 'Campo obrigatório' })
    .refine(value => !value.includes('_'), 'Celular inválido'),
});

type Step3FormSchema = z.infer<typeof ScheduleFormSchema>;

export const ScheduleForm = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { step1, step2, step3 } = useAppSelector(state => state.scheduleSlicer);
  const [fetchForm, { isLoading }] = useCreateNewScheduleMutation();
  const toast = useToast();
  const [showModal, setShowModal] = useState(false);
  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    setError,
    clearErrors,
    watch,
    formState: { errors },
  } = useForm<Step3FormSchema>({
    resolver: zodResolver(ScheduleFormSchema),
  });

  const responseId = useRef<string>('');

  const values = {
    time: watch('scheduleTime'),
    date: watch('scheduleDate'),
  };

  const handleClose = () => {
    setShowModal(false);
    navigate('/agendamentos');
  };

  const onSubmit = async (data: Step3FormSchema) => {
    try {
      const id = await fetchForm(
        Object.assign({}, step1, step2, { ...data, email: data.email || '' }),
      ).unwrap();
      responseId.current = id;
      setShowModal(true);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      toast({
        position: 'top',
        title: 'Error',
        description: `Algo deu errado. ${error?.data?.msn_errors?.[0] || ''}`,
        status: 'error',
        duration: 9000,
        isClosable: true,
      });
    }
  };

  const handlePreviousStep = () => {
    dispatch(setStep3({ ...getValues(), email: getValues('email') || '' }));
    dispatch(setCurrentStep(2));
    navigate('/novo-agendamento/servicos');
  };

  useEffect(() => {
    if (addDays(new Date(), 1) > new Date(`${values?.date} ${values?.time}`)) {
      setError('scheduleTime', {
        type: 'custom',
        message: 'Os agendamentos são marcados com 24 horas de antecedência.',
      });
    } else clearErrors('scheduleTime');
  }, [values?.time, values?.date]);

  return (
    <DelayAnimation>
      <NewScheduleHeader />
      <ConfirmModal
        isOpen={showModal}
        onClose={handleClose}
        plate={step1.plate || 'Não Definida'}
        date={watch('scheduleDate')}
        id={responseId.current}
      />

      <VStack
        w="100%"
        display="flex"
        align="center"
        justify="center"
        pt="44px"
        pb="44px"
      >
        <Flex flexDir="column" align="center" justify="center" mb="32px">
          <Text fontSize="18px" color="text.100">
            Informe os dados para agendamento
          </Text>
          <Text fontSize="14px" color="text.300" mt="3px">
            Insira os dados abaixo
          </Text>
        </Flex>

        <Card maxW="400px">
          <Flex flexDir="column" gap="24px" mt="32px">
            <Wrap>
              <Text as="label" fontSize="14px" color="text.300">
                *Endereço de referência sugerido
              </Text>
              <Autocomplete
                // move to .env
                apiKey="AIzaSyAnizZUvtAsW4bb2uO-crScROEzu4md5vU"
                placeholder="Digite a endereço de referência sugerido"
                style={{
                  width: '100%',
                  height: '44px',
                  padding: '17px 20px',
                  border: '1px solid gray',
                  color: '#000000',
                  background: 'transparent',
                  borderRadius: '4px',
                  outline: 'transparent',
                }}
                onPlaceSelected={place => {
                  const { formatted_address } = place;
                  setValue('address', formatted_address);
                }}
                options={{
                  types: ['address'],
                  componentRestrictions: { country: 'br' },
                }}
              />

              {errors.address && (
                <Text
                  alignSelf="start"
                  mt="-18px"
                  pl="8px"
                  color="negative"
                  fontSize="sm"
                >
                  {errors.address?.message}
                </Text>
              )}
            </Wrap>

            <Wrap>
              <Text as="label" fontSize="14px" color="text.300">
                *Data
              </Text>

              <Input
                type="date"
                {...register('scheduleDate')}
                placeholder="Digite data e hora sugerida"
                defaultValue={step3?.scheduleDate || ''}
                maxLength={7}
                min={addDays(new Date(), 1).toISOString().split('T')[0]}
                bg="transparent"
                borderColor="gray.100"
                color="text.100"
                fontSize="16px"
                fontWeight="500"
                size="lg"
                css={css`
                  ::-webkit-calendar-picker-indicator {
                    filter: invert(100%) sepia(0%) saturate(3207%)
                      hue-rotate(130deg) brightness(95%) contrast(80%);
                    width: 100%;
                    position: absolute;
                    padding-left: 308px;
                  }
                `}
              />
              {errors.scheduleDate && (
                <Text
                  alignSelf="start"
                  mt="-18px"
                  pl="8px"
                  color="negative"
                  fontSize="sm"
                >
                  {errors.scheduleDate?.message}
                </Text>
              )}
            </Wrap>

            <SimpleInput
              label="*Horário sugerido"
              labelColor="text.300"
              type="time"
              {...register('scheduleTime')}
              size="lg"
              placeholder="Digite nome do condutor"
              defaultValue={step3?.scheduleTime || ''}
              bg="transparent"
              borderColor="gray.100"
              color="text.100"
              fontSize="16px"
              fontWeight="500"
              error={errors.scheduleTime?.message}
              css={css`
                ::-webkit-calendar-picker-indicator {
                  filter: invert(100%) sepia(0%) saturate(3207%)
                    hue-rotate(130deg) brightness(95%) contrast(80%);
                  width: 100%;
                  position: absolute;
                  padding-left: 298px;
                }
              `}
            />

            <SimpleInput
              label="*Nome / Condutor"
              labelColor="text.300"
              type="email"
              {...register('name')}
              size="lg"
              placeholder="Digite nome do condutor"
              defaultValue={step3?.name || ''}
              bg="transparent"
              borderColor="gray.100"
              color="text.100"
              fontSize="16px"
              fontWeight="500"
              error={errors.name?.message}
            />

            <SimpleInput
              label="E-mail"
              labelColor="text.300"
              type="email"
              {...register('email')}
              size="lg"
              placeholder="Digite o e-mail"
              defaultValue={step3?.email || ''}
              bg="transparent"
              borderColor="gray.100"
              color="text.100"
              fontSize="16px"
              fontWeight="500"
              error={errors.email?.message}
            />

            <SimpleInput
              label="*Celular"
              labelColor="text.300"
              type="phone"
              {...register('phone')}
              id="phone"
              size="lg"
              placeholder="Digite o telefone do condutor"
              defaultValue={step3?.phone || ''}
              bg="transparent"
              borderColor="gray.100"
              color="text.100"
              fontSize="16px"
              fontWeight="500"
              error={errors.phone?.message}
            />
          </Flex>
          <Flex mt="32px" justify="end" gap="36px">
            <Button
              rounded="4px"
              variant="outline"
              p="5"
              onClick={handlePreviousStep}
            >
              <Text color="button">Voltar</Text>
            </Button>

            <Button
              rounded="4px"
              bg="button"
              h="44px"
              onClick={handleSubmit(onSubmit)}
              disabled={isLoading}
              isLoading={isLoading}
            >
              <Text>Finalizar</Text>
            </Button>
          </Flex>
        </Card>
      </VStack>
    </DelayAnimation>
  );
};
